'use client';

import React, { useState } from 'react';
import {
    LayoutDashboard,
    School,
    FileText,
    Settings,
    ChevronDown,
    ChevronRight,
    Baby,
    Library,
    BookOpen,
    Briefcase,
    Users,
    GraduationCap,
    Info,
    Wallet,
    BarChart3,
    ArrowLeft,
    Banknote,
    FileStack,
    Calculator,
    PieChart,
    ArrowRightLeft,
    LucideIcon,
    Receipt,
    HandCoins,
    CreditCard,
    TrendingUp,
    ShieldCheck,
    GitBranch,
    UserCheck,
    Settings2,
    Search,
} from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

interface NavChild {
    name: string;
    href: string;
    icon: LucideIcon;
}

interface NavItem {
    name: string;
    icon: LucideIcon;
    href?: string;
    children?: NavChild[];
}

interface NavGroup {
    group: string;
    items: NavItem[];
}

const yayasanNavItems: NavGroup[] = [
    {
        group: 'Overview',
        items: [
            { name: 'Dashboard Yayasan', icon: LayoutDashboard, href: '/yayasan' },
        ]
    },
    {
        group: 'Manajemen Wilayah',
        items: [
            {
                name: 'List Sekolah',
                icon: School,
                children: [
                    { name: 'PAUD Finance', href: '/yayasan/paud', icon: Baby },
                    { name: 'SD Finance', href: '/yayasan/sd', icon: Library },
                    { name: 'SMP Finance', href: '/yayasan/smp', icon: BookOpen },
                    { name: 'SMK Finance', href: '/yayasan/smk', icon: Briefcase },
                ]
            },
        ]
    },
    {
        group: 'Sistem Global',
        items: [
            {
                name: 'Master Data',
                icon: Settings2,
                children: [
                    { name: 'Role & Permissions', href: '/yayasan/master-data/roles', icon: ShieldCheck },
                    { name: 'Mengelola Akun', href: '/yayasan/master-data/accounts', icon: UserCheck },
                    { name: 'Jenjang Approval', href: '/yayasan/master-data/approval', icon: GitBranch },
                ]
            },
        ]
    }
];

const sekolahNavItems: NavGroup[] = [
    {
        group: 'Menu Utama',
        items: [
            { name: 'Overview', icon: LayoutDashboard, href: '/sekolah' },
        ]
    },
    {
        group: 'Data Sekolah',
        items: [
            { name: 'Informasi Dasar', icon: Info, href: '/sekolah/informasi-dasar' },
            { name: 'Data Guru', icon: Users, href: '/sekolah/guru' },
            { name: 'Data Staff', icon: Briefcase, href: '/sekolah/staff' },
            { name: 'Data Siswa', icon: GraduationCap, href: '/sekolah/siswa' },
        ]
    },
    {
        group: 'Keuangan',
        items: [
            {
                name: 'Keuangan',
                icon: Wallet,
                children: [
                    { name: 'Data List Akun', href: '/sekolah/keuangan/akun', icon: FileStack },
                    { name: 'Rekening Bank', href: '/sekolah/keuangan/rekening', icon: Banknote },
                    { name: 'Setoran', href: '/sekolah/keuangan/setoran', icon: HandCoins },
                    { name: 'Transfer Kas', href: '/sekolah/keuangan/transfer', icon: ArrowRightLeft },
                    { name: 'Pengeluaran biaya', href: '/sekolah/keuangan/pengeluaran', icon: Receipt },
                    { name: 'Permintaan Cas laci', href: '/sekolah/keuangan/cas-laci', icon: CreditCard },
                    { name: 'Setoran Modal', href: '/sekolah/keuangan/setoran-modal', icon: TrendingUp },
                ]
            },
        ]
    },
    {
        group: 'Laporan',
        items: [
            {
                name: 'Laporan',
                icon: BarChart3,
                children: [
                    { name: 'Pelaporan Unit', href: '/sekolah/laporan', icon: LayoutDashboard },
                    { name: 'Jurnal', href: '/sekolah/laporan/jurnal', icon: FileText },
                    { name: 'Buku Besar', href: '/sekolah/laporan/buku-besar', icon: Library },
                    { name: 'Laba Rugi', href: '/sekolah/laporan/laba-rugi', icon: PieChart },
                    { name: 'Perubahan Modal', href: '/sekolah/laporan/modal', icon: BarChart3 },
                    { name: 'Neraca', href: '/sekolah/laporan/neraca', icon: Calculator },
                ]
            },
        ]
    }
];

const intrayNavItems: NavGroup[] = [
    {
        group: 'System Admin',
        items: [
            { name: 'Dashboard Intray', icon: LayoutDashboard, href: '/intray' },
            { name: 'Integrasi Sistem', icon: ArrowRightLeft, href: '/intray/integrasi' },
            { name: 'Log Aktivitas', icon: FileText, href: '/intray/logs' },
            { name: 'Pengaturan Global', icon: Settings, href: '/intray/settings' },
        ]
    }
];

interface SidebarProps {
    className?: string;
    onClose?: () => void;
    collapsed?: boolean;
    setCollapsed?: (val: boolean) => void;
}

export default function Sidebar({ className = "", onClose, collapsed = false, setCollapsed }: SidebarProps) {
    const pathname = usePathname();
    const [openSubMenus, setOpenSubMenus] = useState<string[]>(['List Sekolah', 'Keuangan', 'Laporan']);

    const isYayasanContext = pathname.startsWith('/yayasan');
    const isSekolahContext = pathname.startsWith('/sekolah');
    const isIntrayContext = pathname.startsWith('/intray');

    const schoolMatch = pathname.match(/^\/yayasan\/(paud|paud-pekanbaru|paud-kampar|paud-siak|sd|smp|smk)(\/.*)?$/);
    const isSchoolUnitContext = !!schoolMatch || pathname.startsWith('/sekolah');
    const schoolType = schoolMatch ? schoolMatch[1] : null;
    const schoolBasePath = schoolMatch ? `/yayasan/${schoolType}` : '/sekolah';

    const toggleSubMenu = (name: string) => {
        setOpenSubMenus(prev =>
            prev.includes(name) ? prev.filter(i => i !== name) : [...prev, name]
        );
    };

    const isSubMenuOpen = (name: string) => openSubMenus.includes(name);

    const getDashboardTitle = () => {
        if (isIntrayContext) return 'Intray System';
        if (isYayasanContext) return 'Yayasan Finance';
        return 'School Finance';
    };

    const getDashboardSubtitle = () => {
        if (isSchoolUnitContext) return 'Unit Management';
        if (isIntrayContext) return 'System Admin';
        return 'Global Management';
    };

    const getItemsToRender = (): NavGroup[] => {
        if (isIntrayContext) return intrayNavItems;
        if (isSchoolUnitContext) {
            return sekolahNavItems.map(group => ({
                ...group,
                items: group.items.map(item => ({
                    ...item,
                    href: item.href?.replace('/sekolah', schoolBasePath),
                    children: item.children?.map((child: NavChild) => ({
                        ...child,
                        href: child.href.replace('/sekolah', schoolBasePath)
                    }))
                }))
            }));
        }
        return yayasanNavItems;
    };

    const itemsToRender = getItemsToRender();

    return (
        <aside
            className={`transition-all duration-500 ease-in-out bg-surface border-r border-border-main hidden lg:flex flex-col shadow-2xl shadow-primary/5 ${collapsed ? 'w-24' : 'w-72'} ${className}`}
        >
            {/* Logo Area */}
            <div className={`p-8 mb-4 transition-all duration-500 flex items-center ${collapsed ? 'justify-center' : 'justify-start'}`}>
                <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-2xl bg-primary flex items-center justify-center shadow-lg shadow-primary/30 shrink-0 group hover:rotate-12 transition-transform">
                        {isSchoolUnitContext ? <School className="text-white w-6 h-6" /> : <LayoutDashboard className="text-white w-6 h-6" />}
                    </div>
                    {!collapsed && (
                        <div className="flex flex-col animate-in fade-in slide-in-from-left-4 duration-500">
                            <span className="font-black text-foreground tracking-tight text-lg uppercase truncate w-40">
                                {getDashboardTitle().split(' ')[0]}<span className="text-primary ml-1">{getDashboardTitle().split(' ')[1]}</span>
                            </span>
                            <span className="text-[9px] font-black text-muted uppercase tracking-[0.3em] -mt-0.5">{getDashboardSubtitle()}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Context Switcher (Only for School Unit in Yayasan) */}
            {isSchoolUnitContext && isYayasanContext && (
                <div className={`px-4 mb-6 ${collapsed ? "justify-center" : ""}`}>
                    <Link
                        onClick={onClose}
                        href="/yayasan"
                        className={`flex items-center gap-4 px-4 py-3 rounded-2xl text-secondary hover:bg-primary-soft hover:text-primary transition-all duration-300 group ${collapsed ? "justify-center" : ""}`}
                    >
                        <ArrowLeft className={`w-4 h-4 transition-transform group-hover:-translate-x-1`} />
                        {!collapsed && (
                            <span className="text-[10px] font-black uppercase tracking-widest leading-none">
                                Kembali ke Pusat
                            </span>
                        )}
                    </Link>
                </div>
            )}

            {/* Navigation Items */}
            <nav className="flex-1 overflow-y-auto px-4 space-y-8 custom-scrollbar pb-10">
                {itemsToRender.map((group, groupIdx) => (
                    <div key={groupIdx} className="space-y-4">
                        {!collapsed && group.group && (
                            <h3 className="px-4 text-[10px] font-black text-muted uppercase tracking-[0.2em] opacity-50">
                                {group.group}
                            </h3>
                        )}
                        <div className="space-y-1.5">
                            {group.items.map((item, itemIdx) => {
                                const hasChildren = item.children && item.children.length > 0;
                                const isActive = item.href ? pathname === item.href : item.children?.some(child => pathname === child.href);
                                const isOpen = isSubMenuOpen(item.name);

                                return (
                                    <div key={itemIdx} className="space-y-1">
                                        {item.href && !hasChildren ? (
                                            <Link
                                                href={item.href}
                                                onClick={onClose}
                                                className={`flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all duration-300 group relative ${isActive
                                                    ? 'bg-primary text-white shadow-xl shadow-primary/20 translate-x-1'
                                                    : 'text-secondary hover:bg-primary-soft hover:text-primary'
                                                    }`}
                                            >
                                                <item.icon className={`w-5 h-5 transition-transform duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                                                {!collapsed && <span className="text-xs font-bold leading-none">{item.name}</span>}
                                                {isActive && (
                                                    <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                                                )}
                                            </Link>
                                        ) : (
                                            <div className="space-y-1">
                                                <button
                                                    onClick={() => toggleSubMenu(item.name)}
                                                    className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all duration-300 group ${isActive && !isOpen ? 'bg-primary-soft text-primary' : 'text-secondary hover:bg-primary-soft hover:text-primary'
                                                        }`}
                                                >
                                                    <item.icon className={`w-5 h-5 transition-transform duration-300 ${isActive ? 'text-primary scale-110' : 'group-hover:scale-110'}`} />
                                                    {!collapsed && (
                                                        <>
                                                            <span className="text-xs font-bold grow text-left truncate">{item.name}</span>
                                                            {isOpen ? (
                                                                <ChevronDown className="w-4 h-4 transition-transform duration-300" />
                                                            ) : (
                                                                <ChevronRight className="w-4 h-4 transition-transform duration-300" />
                                                            )}
                                                        </>
                                                    )}
                                                </button>
                                                {isOpen && !collapsed && (
                                                    <div className="ml-6 space-y-1 border-l-2 border-primary/10 pl-4 animate-in slide-in-from-top-2 duration-300">
                                                        {item.children?.map((child, childIdx) => {
                                                            const isChildActive = pathname === child.href;
                                                            return (
                                                                <Link
                                                                    key={childIdx}
                                                                    href={child.href}
                                                                    onClick={onClose}
                                                                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${isChildActive
                                                                        ? 'text-primary font-black bg-primary-soft'
                                                                        : 'text-muted hover:text-primary hover:bg-primary-soft/30'
                                                                        }`}
                                                                >
                                                                    <child.icon className={`w-3.5 h-3.5 ${isChildActive ? 'text-primary' : ''}`} />
                                                                    <span className="text-[11px] font-bold truncate">{child.name}</span>
                                                                </Link>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </nav>

            {/* Bottom Card (Only for Yayasan Home) */}
            {!collapsed && isYayasanContext && !isSchoolUnitContext && (
                <div className="p-6">
                    <div className="bg-primary rounded-3xl p-6 text-white relative overflow-hidden shadow-2xl shadow-primary/20">
                        <div className="relative z-10">
                            <p className="text-[10px] font-black text-white/70 uppercase tracking-widest mb-1.5 text-center">TOTAL KAS GLOBAL</p>
                            <h3 className="text-lg font-black truncate text-center leading-none">Rp 1.571.900.000</h3>
                            <button className="mt-4 w-full bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-xl py-2.5 text-[9px] font-black uppercase tracking-widest transition-all">
                                Detail Rekening
                            </button>
                        </div>
                        <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
                    </div>
                </div>
            )}
        </aside>
    );
}
